import type { TypeRunWithConcurrencyUploadOutcome } from '../../../async/runWithConcurrencyUpload';

import type { TypeUploadOnceResult } from '../types';

// ----------------------------------------------------------------------------

/*
 * Turns the last attempt's result into the outcome the orchestrator buckets on.
 *
 * An attempt carrying a server response is reported as the failure it is, even when the abort flag
 * went up in the meantime: the caller's own fnFail handler may have raised it (see `fnAbortGroup`),
 * and reclassifying the result as an abort would drop the very status that caused it. A request
 * aborted by the run never reaches here as `failed` — the transport resolves those as `abort`.
 */
function classifyOutcome(
    result: TypeUploadOnceResult | undefined,
    isAborted: () => boolean,
    isAbortedRequest?: (xhr?: XMLHttpRequest) => boolean,
): TypeRunWithConcurrencyUploadOutcome {
    if (!result) {
        return isAborted() ? 'aborted' : 'error';
    }

    if (result.ok) {
        return 'ok';
    }

    const hasResponse = result.reason === 'failed' && (result.status ?? 0) > 0;

    if (!hasResponse && (result.reason === 'abort' || isAborted() || isAbortedRequest?.(result.xhr))) {
        return 'aborted';
    }

    return 'error';
}

// ----------------------------------------------------------------------------

export default classifyOutcome;
